import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUser } from '../../../services/authService';
import axios from '../../../utils/axiosInstance';

// 다크 모드 스타일 상수
const darkModeStyles = {
  container: {
    p: 0, 
    background: 'linear-gradient(180deg, rgba(25,25,38,0.95) 0%, rgba(25,32,48,0.95) 100%)',
    minHeight: '100vh'
  },
  paper: {
    p: { xs: 1, sm: 2, md: 3 },
    m: { xs: 0.5, sm: 1, md: 2 },
    background: 'linear-gradient(145deg, rgba(35,35,50,0.9) 0%, rgba(35,45,65,0.85) 100%)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    borderRadius: { xs: '8px', sm: '12px', md: '16px' },
    border: '1px solid rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(8px)',
  },
  title: {
    color: '#fff',
    fontSize: '28px',
    fontWeight: 'bold',
    mb: 1,
    letterSpacing: '0.5px',
    textShadow: '0 2px 4px rgba(0,0,0,0.3)'
  },
  textField: {
    '& .MuiInputBase-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '8px',
      color: '#fff'
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.15)'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.3)'
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#60a5fa'
    }
  },
  formControl: {
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)'
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '8px',
      color: '#fff'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.15)'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.3)'
    },
    '& .MuiSelect-icon': {
      color: 'rgba(255, 255, 255, 0.7)'
    }
  },
  menuItem: {
    '&.MuiMenuItem-root': {
      color: '#fff',
      backgroundColor: 'rgba(35, 35, 50, 0.95)',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      },
      '&.Mui-selected': {
        backgroundColor: 'rgba(96, 165, 250, 0.2)',
        '&:hover': {
          backgroundColor: 'rgba(96, 165, 250, 0.3)',
        }
      }
    }
  },
  searchButton: {
    backgroundColor: '#60a5fa',
    color: '#fff',
    borderRadius: '10px',
    padding: '10px 24px',
    boxShadow: '0 4px 10px rgba(33, 150, 243, 0.3)',
    fontWeight: 'bold',
    textTransform: 'none',
    minWidth: '120px',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#3b82f6',
      boxShadow: '0 6px 12px rgba(30, 136, 229, 0.4)',
      transform: 'translateY(-2px)'
    }
  },
  tableContainer: {
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    border: '1px solid rgba(255, 255, 255, 0.08)'
  },
  tableHead: {
    backgroundColor: 'rgba(45, 45, 65, 0.9)',
    '& .MuiTableCell-head': {
      color: '#fff',
      fontWeight: 'bold',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
    }
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: 'rgba(255, 255, 255, 0.02)'
    },
    '&:nth-of-type(even)': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)'
    },
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      transition: 'background-color 0.3s ease'
    },
    '& .MuiTableCell-body': {
      color: '#fff',
      borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
    }
  },
  tableCell: {
    color: '#fff',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
  },
  loadingIndicator: {
    color: '#60a5fa',
    margin: '40px 0'
  },
  noDataMessage: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontStyle: 'italic',
    padding: '40px 0'
  },
  tablePagination: {
    marginTop: '12px',
    borderRadius: '12px',
    backgroundColor: 'rgba(45, 45, 65, 0.8)',
    color: '#fff',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    '& .MuiTablePagination-toolbar': {
      justifyContent: 'center'
    },
    '& .MuiTablePagination-select': {
      padding: '6px 10px',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '8px',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      color: '#fff',
      fontWeight: 'bold',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      },
      '& .MuiSelect-icon': {
        color: 'rgba(255, 255, 255, 0.7)',
      }
    },
    '& .MuiTablePagination-actions': {
      color: '#fff',
      '& .MuiIconButton-root': {
        color: 'rgba(255, 255, 255, 0.7)',
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },
        '&.Mui-disabled': {
          color: 'rgba(255, 255, 255, 0.3)',
        }
      }
    },
    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
      color: 'rgba(255, 255, 255, 0.7)'
    }
  },
  errorMessage: {
    color: '#f87171',
    backgroundColor: 'rgba(248, 113, 113, 0.1)',
    border: '1px solid rgba(248, 113, 113, 0.2)',
    borderRadius: '8px',
    padding: '10px 16px',
    mb: 2
  },
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'rgba(35, 35, 50, 0.95)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
    color: '#fff'
  },
  modalTitle: {
    color: '#fff',
    fontWeight: 'bold',
    mb: 2
  },
  modalTextField: {
    '& .MuiInputBase-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '8px',
      color: '#fff'
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.15)'
    },
    mb: 1
  },
  errorText: {
    color: '#f87171',
    fontSize: '0.875rem',
    mb: 2,
    px: 1
  },
  cancelButton: {
    color: '#fff',
    borderColor: 'rgba(255, 255, 255, 0.3)',
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.5)',
      backgroundColor: 'rgba(255, 255, 255, 0.05)'
    }
  },
  submitButton: {
    backgroundColor: '#60a5fa',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#3b82f6'
    }
  }
};

const UserList = () => {
  const navigate = useNavigate();
  const isNonMobile = useMediaQuery('(min-width:600px)');

  const [filters, setFilters] = useState({
    agentId: '',
    userId: '',
    nickname: '',
  });

  const [memberList, setMemberList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [agents, setAgents] = useState([]);

  useEffect(() => {
    const fetchAgentList = async () => {
      setLoading(true);
      try {
        const user = getUser();
        console.log('로그인 사용자 정보:', user);

        if (!user || !user.id) {
          setError('로그인 정보를 찾을 수 없습니다.');
          return;
        }

        setCurrentUser(user);

        if (user.grade !== 'admin') {
          setFilters(prev => ({ ...prev, agentId: user.id }));

          try {
            const response = await axios.get('/agent/children');
            if (response.data.success) {
              const agentTree = response.data.agent_tree;
              const agentOptions = [
                { id: agentTree.id, name: agentTree.id, agent_srl: 0, level: 0 },
                ...agentTree.children.map(child => ({
                  id: child.id,
                  name: child.id,
                  level: child.level,
                })),
              ];
              setAgents(agentOptions);
            }
          } catch (error) {
            console.error('에이전트 목록 불러오기 실패:', error);
          }
        }

        const response = await axios.get('/members', {
          params: {
            per_page: 30,
            agent_id: user.id,
          },
        });

        if (response.status) {
          setMemberList(response.data);
        } else {
          console.error('에이전트 목록 불러오기 실패:', response.statusText);
        }
      } catch (error) {
        console.error('에이전트 목록 불러오기 중 오류 발생:', error);
        setError('에이전트 목록을 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchAgentList();
  }, []);

  const [selectedUser, setSelectedUser] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [openModal, setOpenModal] = useState(false);

  const handleFilterChange = (field, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [field]: value,
    }));
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const user = getUser();
      console.log('검색 조건:', filters);

      const searchParams = {
        per_page: rowsPerPage,
        page: page + 1,
      };

      if (user.grade === 'admin') {
        if (filters.agentId) {
          searchParams.agent_id = filters.agentId;
        } else {
          searchParams.agent_id = user.id;
        }
      } else {
        searchParams.agent_id = user.id;
      }

      if (filters.userId) searchParams.user_id = filters.userId;
      if (filters.nickname) searchParams.nickname = filters.nickname;

      const response = await axios.get('/members', { params: searchParams });

      if (response.status) {
        setMemberList(response.data);
        console.log('검색 결과:', response.data);
      } else {
        console.error('검색 실패:', response.statusText);
        setError('검색에 실패했습니다.');
      }
    } catch (error) {
      console.error('검색 중 오류 발생:', error);
      setError('검색 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentClick = user => {
    setSelectedUser(user);
    setPaymentAmount('');
    setOpenModal(true);
  };

  const handlePaymentSubmit = () => {
    console.log('지급 대상:', selectedUser);
    console.log('지급 금액:', paymentAmount);
    setOpenModal(false);
  };

  const handleCancel = () => {
    setOpenModal(false);
  };

  const handleEditClick = userId => {
    navigate(`/user-detail/${userId}`);
  };

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatNumber = (number) => {
    if (number === null || number === undefined) return '0';
    return number.toLocaleString('ko-KR');
  };

  return (
    <Box sx={darkModeStyles.container}>
      <Paper sx={darkModeStyles.paper}>
        <Typography variant="h4" sx={darkModeStyles.title}>
          유저 목록
        </Typography>

        <Box display="flex" flexDirection={isNonMobile ? 'row' : 'column'} gap={2} sx={{ mb: 3 }}>
          {currentUser && currentUser.grade === 'admin' ? (
            <TextField 
              label="에이전트 아이디" 
              value={filters.agentId} 
              onChange={e => handleFilterChange('agentId', e.target.value)} 
              fullWidth 
              sx={darkModeStyles.textField}
            />
          ) : (
            <FormControl fullWidth sx={darkModeStyles.formControl}>
              <InputLabel>에이전트 아이디</InputLabel>
              <Select 
                value={filters.agentId} 
                onChange={e => handleFilterChange('agentId', e.target.value)}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: 'rgba(35, 35, 50, 0.95)',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px'
                    }
                  }
                }}
              >
                {agents.map(agent => (
                  <MenuItem
                    key={agent.id}
                    value={agent.id}
                    sx={{
                      ...darkModeStyles.menuItem,
                      backgroundColor: agent.level === 0 ? 'rgba(96, 165, 250, 0.1)' : 'inherit',
                      fontWeight: agent.level === 0 ? 600 : 400,
                      '&:hover': {
                        backgroundColor: agent.level === 0 ? 'rgba(96, 165, 250, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: agent.level === 0 ? 'rgba(96, 165, 250, 0.3)' : 'rgba(96, 165, 250, 0.2)',
                      }
                    }}
                  >
                    {agent.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField 
            label="유저 아이디" 
            value={filters.userId} 
            onChange={e => handleFilterChange('userId', e.target.value)} 
            fullWidth
            sx={darkModeStyles.textField}
          />

          <TextField 
            label="유저 닉네임" 
            value={filters.nickname} 
            onChange={e => handleFilterChange('nickname', e.target.value)} 
            fullWidth
            sx={darkModeStyles.textField}
          />

          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={loading}
            sx={{
              ...darkModeStyles.searchButton,
              height: isNonMobile ? 'auto' : 56,
              alignSelf: isNonMobile ? 'flex-start' : 'stretch'
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon sx={{ fontSize: 20, mr: 1 }} />}
            {loading ? '로딩 중...' : '검색'}
          </Button>
        </Box>

        {error && (
          <Box sx={darkModeStyles.errorMessage}>
            <Typography>{error}</Typography>
          </Box>
        )}

        <TableContainer sx={darkModeStyles.tableContainer}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="200px">
              <CircularProgress sx={darkModeStyles.loadingIndicator} />
            </Box>
          ) : memberList?.data?.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100px">
              <Typography sx={darkModeStyles.noDataMessage}>데이터가 없습니다.</Typography>
            </Box>
          ) : (
            <Table>
              <TableHead sx={darkModeStyles.tableHead}>
                <TableRow>
                  <TableCell sx={darkModeStyles.tableCell}>회원 번호</TableCell>
                  <TableCell sx={darkModeStyles.tableCell}>소속 에이전트</TableCell>
                  <TableCell sx={darkModeStyles.tableCell}>유저</TableCell>
                  <TableCell sx={darkModeStyles.tableCell}>현재 포인트</TableCell>
                  <TableCell sx={darkModeStyles.tableCell}>가입 시작</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {memberList?.data?.map((row, idx) => (
                  <TableRow key={idx} sx={darkModeStyles.tableRow}>
                    <TableCell sx={darkModeStyles.tableCell}>{row.user_srl}</TableCell>
                    <TableCell sx={darkModeStyles.tableCell}>{row.agent_id}</TableCell>
                    <TableCell sx={darkModeStyles.tableCell}>{row.user_id}</TableCell>
                    <TableCell sx={darkModeStyles.tableCell}>{formatNumber(row.cur_point)}</TableCell>
                    <TableCell sx={darkModeStyles.tableCell}>{row.created_at}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {!loading && memberList?.data?.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[10, 20, 30, 50, 100]}
              component="div"
              count={memberList?.meta?.total ?? 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="페이지당 행 수"
              sx={darkModeStyles.tablePagination}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      backgroundColor: 'rgba(35, 35, 50, 0.95)',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      '& .MuiMenuItem-root': {
                        color: '#fff',
                        padding: '8px 16px',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(96, 165, 250, 0.2)',
                        }
                      },
                    },
                  },
                },
              }}
            />
          )}
        </TableContainer>
      </Paper>

      <Modal open={openModal} onClose={handleCancel}>
        <Box sx={darkModeStyles.modal}>
          <Typography variant="h6" sx={darkModeStyles.modalTitle}>
            지급 처리
          </Typography>
          <TextField 
            label="지급 금액" 
            value={paymentAmount} 
            onChange={e => setPaymentAmount(e.target.value)} 
            fullWidth 
            sx={darkModeStyles.modalTextField}
          />
          <Typography variant="body2" sx={darkModeStyles.errorText}>
            마이너스 (-)로 입력시 보유 금액을 차감합니다. <br />
            에이전트 보유 금액보다 큰 금액을 지급할 수 없습니다. <br />
            대상 유저의 보유 금액보다 큰 금액을 차감할 수 없습니다.
          </Typography>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" onClick={handleCancel} sx={darkModeStyles.cancelButton}>
              취소
            </Button>

            <Button variant="contained" onClick={handlePaymentSubmit} sx={darkModeStyles.submitButton}>
              지급
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default UserList;